import { Dispatch } from "redux/commonTypes";

import purchaseApi from "./purchaseApi";
import profileActions from "../profile/profileActions";
import { restMilliSeconds } from "utils/helpers/restMilliseconds";

import {
  actionTypes,
  buySuccessType,
  currentBuyType,
  priceType,
  somePremiumsType,
} from "./purchaseTypes";


const Actions = {
  setBillingAmounts: (amounts: Array<any>) => ({
    type: actionTypes.SET_BILLING_AMOUNTS,
    payload: amounts,
  }),
  setVelvetCatalog: (catalog: any) => ({
    type: actionTypes.SET_VELVET_CATALOG,
    payload: catalog,
  }),
  setBillingRedirect: (link: string) => ({
    type: actionTypes.SET_BILLING_REDIRECT,
    payload: link,
  }),
  createPayment: (amount: number) => (dispatch: Dispatch) => {
    purchaseApi
      .createPayment(amount)
      .then((resp) => {
        dispatch(Actions.setBillingRedirect(resp.data.id));
      })
  },
  getBillingAmounts: () => (dispatch: Dispatch) => {
    purchaseApi
      .getBillingAmounts()
      .then((resp) =>
        dispatch(
          Actions.setBillingAmounts(resp.data.amounts || [1000, 5000, 10000])
        )
      )
  },
  //BALANCE
  setBalance: (balance: number) => ({
    type: actionTypes.SET_BALANCE,
    payload: balance,
  }),
  setBalanceLoading: (status: boolean) => ({
    type: actionTypes.SET_BALANCE_LOADING,
    payload: status,
  }),
  getBalance: () => (dispatch: Dispatch) => {
    dispatch(Actions.setBalanceLoading(true));
    purchaseApi
      .getVelvetEntitlement()
      .then((response) => {
        const ent = response.data;
        dispatch(Actions.setBoosts(ent.boosts ?? 0));
        dispatch(Actions.setSomePremiums({
          hiddenRest: ent.invisibleDeadline || null,
          onlineRest: ent.active ? (ent.expiresAt || null) : null,
          pinnedRest: ent.pinnedDeadline || null,
          premiumRest: ent.active ? (ent.expiresAt || null) : null,
        }));
        dispatch(Actions.setBalanceLoading(false));
      })
      .catch(() => {
        dispatch(Actions.setBalanceLoading(false));
      });
  },
  setSomePremiums: (rest: somePremiumsType) => ({
    type: actionTypes.SET_SOME_PREMIUMS,
    payload: {
      hiddenRest: restMilliSeconds(rest.hiddenRest as string),
      onlineRest: restMilliSeconds(rest.onlineRest as string),
      pinnedRest: restMilliSeconds(rest.pinnedRest as string),
      premiumRest: restMilliSeconds(rest.premiumRest as string),
      place: rest.place
    },
  }),
  //PRICES
  setPremiumPrices: (prices: Array<priceType>) => ({
    type: actionTypes.SET_PREMIUM_PRICES,
    payload: prices,
  }),
  setTopPrices: (prices: Array<priceType>) => ({
    type: actionTypes.SET_TOP_PRICES,
    payload: prices,
  }),
  setHiddenPrices: (prices: Array<priceType>) => ({
    type: actionTypes.SET_HIDDEN_PRICES,
    payload: prices,
  }),
  setOnlinePrices: (prices: Array<priceType>) => ({
    type: actionTypes.SET_ONLINE_PRICES,
    payload: prices,
  }),
  setUpPrices: (prices: Array<priceType>) => ({
    type: actionTypes.SET_UP_PRICES,
    payload: prices,
  }),
  getPremiumPrices: () => (dispatch: Dispatch) => {
    purchaseApi
      .getPremiumPriceList()
      .then((resp) =>
        dispatch(Actions.setPremiumPrices(resp.data.premiumsPriceList))
      );
  },
  // Загрузка каталога Velvet для пэйвола Basic/Prime (витрина). Покупка через
  // веб-платёжки будет прикручена позже по offerId из офферов каталога.
  getVelvetCatalog: () => (dispatch: Dispatch) => {
    const locale = window.localStorage.i18nextLng || "en";
    purchaseApi
      .getVelvetCatalog(locale)
      .then((resp) => dispatch(Actions.setVelvetCatalog(resp.data)))
      .catch(() => dispatch(Actions.setVelvetCatalog(null)));
  },
  getTopPrices: () => (dispatch: Dispatch) => {
    purchaseApi
      .getPinnedProfilePriceList()
      .then((resp) => {
        resp.data.freePrice?.paymentFeature
          ? dispatch(
              Actions.setTopPrices([
                ...resp.data.priceList,
                {
                  ...resp.data.freePrice?.paymentFeature,
                  isFree: true,
                  coolDown: resp.data.freePrice?.timeAvailableActivation,
                },
              ])
            )
          : dispatch(Actions.setTopPrices(resp.data.priceList));
      })
  },
  getHiddenPrices: () => (dispatch: Dispatch) => {
    purchaseApi
      .getInvisiblePriceList()
      .then((resp) => {
        resp.data.freePrice?.paymentFeature
          ? dispatch(
              Actions.setHiddenPrices([
                ...resp.data.priceList,
                {
                  ...resp.data.freePrice?.paymentFeature,
                  isFree: true,
                  coolDown: resp.data.freePrice?.timeAvailableActivation,
                },
              ])
            )
          : dispatch(Actions.setHiddenPrices(resp.data.priceList));
      })
  },
  getOnlinePrices: () => (dispatch: Dispatch) => {
    purchaseApi
      .getOnlineFilterPriceList()
      .then((resp) => {
        resp.data.freePrice?.paymentFeature
          ? dispatch(
              Actions.setOnlinePrices([
                ...resp.data.priceList,
                {
                  ...resp.data.freePrice?.paymentFeature,
                  isFree: true,
                  coolDown: resp.data.freePrice?.timeAvailableActivation,
                },
              ])
            )
          : dispatch(Actions.setOnlinePrices(resp.data.priceList));
      })
  },
  getUpPrices: () => (dispatch: Dispatch) => {
    purchaseApi
      .getBoostPriceList()
      .then((resp) => {
        resp.data.freePrice?.paymentFeature
          ? dispatch(
              Actions.setUpPrices([
                ...resp.data.priceList,
                {
                  ...resp.data.freePrice?.paymentFeature,
                  isFree: true,
                  coolDown: resp.data.freePrice?.timeAvailableActivation,
                },
              ])
            )
          : dispatch(Actions.setUpPrices(resp.data.priceList));
      })
  },

  buyPremium: (id: number) => (dispatch: Dispatch) => {
    dispatch(Actions.setBuyLoading(true));
    purchaseApi
      .buyPremium(id)
      .then((resp) => {
        dispatch(Actions.setBuyLoading(false));
        dispatch(Actions.setBuySuccess("Yes"));
      })
      .catch((err) => {
        dispatch(Actions.setBuyLoading(false));
        dispatch(Actions.setBuySuccess("No"));
      });
  },
  buyTop: (id: number) => (dispatch: Dispatch) => {
    dispatch(Actions.setBuyLoading(true));
    purchaseApi
      .buyTop(id)
      .then((resp) => {
        dispatch(Actions.setBuyLoading(false));
        dispatch(Actions.setBuySuccess("Yes"));
      })
      .catch((err) => {
        dispatch(Actions.setBuyLoading(false));
        dispatch(Actions.setBuySuccess("No"));
      });
  },
  buyUp: (id: number) => (dispatch: Dispatch) => {
    dispatch(Actions.setBuyLoading(true));
    purchaseApi
      .buyUp(id)
      .then((resp) => {
        dispatch(Actions.setBuyLoading(false));
        dispatch(Actions.setBuySuccess("Yes"));
        //@ts-ignore
        dispatch(profileActions.getExtendedProfileInfo());
      })
      .catch((err) => {
        dispatch(Actions.setBuyLoading(false));
        dispatch(Actions.setBuySuccess("No"));
      });
  },
  buyHidden: (id: number) => (dispatch: Dispatch) => {
    dispatch(Actions.setBuyLoading(true));
    purchaseApi
      .buyHidden(id)
      .then((resp) => {
        dispatch(Actions.setBuyLoading(false));
        dispatch(Actions.setBuySuccess("Yes"));
      })
      .catch((err) => {
        dispatch(Actions.setBuyLoading(false));
        dispatch(Actions.setBuySuccess("No"));
      });
  },
  buyOnline: (id: number) => (dispatch: Dispatch) => {
    dispatch(Actions.setBuyLoading(true));
    purchaseApi
      .buyOnline(id)
      .then((resp) => {
        dispatch(Actions.setBuyLoading(false));
        dispatch(Actions.setBuySuccess("Yes"));
      })
      .catch((err) => {
        dispatch(Actions.setBuyLoading(false));
        dispatch(Actions.setBuySuccess("No"));
      });
  },
  //BUY
  setCurrentBuy: (buyObj: currentBuyType) => ({
    type: actionTypes.SET_CURRENT_BUY,
    payload: buyObj,
  }),
  setBuyLoading: (status: boolean) => ({
    type: actionTypes.SET_BUY_LOADING,
    payload: status,
  }),
  setBuySuccess: (status: buySuccessType) => ({
    type: actionTypes.SET_BUY_SUCCESS,
    payload: status,
  }),
  //BOOSTS
  useBoost: () => (dispatch: Dispatch) => {
    purchaseApi
      .useBoost()
      .then((resp) => {
        dispatch(Actions.reduceBoosts(1));
        dispatch(Actions.setBoostUsed(true));
      })
  },
  setBoosts: (count: number) => ({
    type: actionTypes.SET_BOOSTS,
    payload: count,
  }),
  reduceBoosts: (count: number) => ({
    type: actionTypes.REDUCE_BOOSTS,
    payload: count,
  }),
  setBoostUsed: (status: boolean) => ({
    type: actionTypes.SET_BOOST_USED,
    payload: status,
  }),
  // Coin bonus removed — endpoints deleted
  getBonusPrice: () => (_dispatch: Dispatch) => {},
  setBonusPrice: (bonus: number, cooldown: string) => ({
    type: actionTypes.SET_BONUS_PRICE,
    payload: { bonus, cooldown },
  }),
  useBonus: () => (_dispatch: Dispatch) => {},
  setBonusUsed: (status: boolean) => ({
    type: actionTypes.SET_BONUS_USED,
    payload: status,
  })
};

export default Actions;
